<script setup lang="ts">
import Product, { type IAttributeGroupMap } from "~/models/Product";
import { computed, ref } from "vue";
import type { RenderParameters } from "pdfjs-dist/types/src/display/api";
import iframeResize from "@iframe-resizer/parent";
import { ProductDescriptionSection } from "~/config/product";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useIntersectionObserver,
  useMounted,
} from "@vueuse/core";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
  initExpanded: {
    type: Boolean,
    default: true,
  },
  openOneAtATime: {
    type: Boolean,
    default: false,
  },
});

const viewer = ref<Node | null>(null);
const viewerCt = ref<Node | null>(null);
const pdfReady = ref(false);
const landingReady = ref(false);
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileSize = breakpoints.smallerOrEqual("md");
const attributeGroupSorted = computed((): IAttributeGroupMap[] => {
  const copy: IAttributeGroupMap[] = [];
  Object.values(props.product.attribute_group_map).forEach((group) =>
    copy.push(group),
  );
  copy.sort((a, b) => b.attributes.length - a.attributes.length);
  return copy;
});

const renderMedia = function () {
  if (props.product.description_file_is_pdf) {
    renderPdfViewer();
  }
  if (props.product.description_landing) {
    iframeResize({ license: "GPLv3", waitForLoad: true }, "#landingIFrame");
  }
};

const attributeGroupColumns = computed(() => {
  const cols = [
    new Map<number, IAttributeGroupMap>(),
    new Map<number, IAttributeGroupMap>(),
  ];
  let currCount = 0;
  let attrsCount = Object.values(attributeGroupSorted.value).length;
  attributeGroupSorted.value.forEach(
    (group) => (attrsCount += group.attributes.length),
  );
  const columnSize = Math.ceil(attrsCount / 2);
  attributeGroupSorted.value.forEach((group, key) => {
    if (currCount <= columnSize || isMobileSize.value) {
      cols[0]?.set(key, group);
    } else {
      cols[1]?.set(key, group);
    }
    currCount += group.attributes.length;
  });
  return cols;
});

const renderPdfViewer = async () => {
  if (!props.product.description_file) {
    return;
  }
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  await import("pdfjs-dist/legacy/build/pdf.worker.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc =
    "pdfjs-dist/legacy/build/pdf.worker.mjs";
  const url: string = props.product.description_file;
  try {
    const pdf = await pdfjs.getDocument(url).promise;
    const raw = await pdf.getData();
    const pages = pdf.numPages;
    for (let n = 1; n <= pages; n++) {
      // Add page number text
      const pageTitle = document.createElement("p");
      pageTitle.className = "text-secondary my-0 p-2 small border-bottom";
      pageTitle.innerText = "Página " + n + " de " + pages;
      const article = document.createElement("article");
      article.className = "p-0";
      // article.appendChild(pageTitle); // quizá sea mejor no añadir número de página
      viewer.value?.appendChild(article);
      (async (n: number, article: HTMLElement) => {
        // cada página necesita su propio contexto de función
        const page = await pdf.getPage(n);

        // Add PDF view
        const canvas = document.createElement("canvas");
        article.prepend(canvas);

        // Render page
        const scale = raw.length / 1024 / 1024 > 3 ? 0.98 : 1.7;
        const viewport = page.getViewport({ scale: scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.style.width = "100%";

        const context = canvas.getContext("2d");
        if (context) {
          const renderContext: RenderParameters = {
            canvasContext: context,
            viewport: viewport,
            canvas: null,
          };
          await page.render(renderContext).promise;
        } else {
          throw "No se pudo obtener el contexto de Canvas.";
        }
        if (n == pages) {
          pdfReady.value = true;
        }
      })(n, article);
    }
  } catch (err) {
    console.log("Error al leer archivo de descripción pdf");
    console.error(err);
    viewerCt.value?.parentNode?.removeChild(viewerCt.value);
  }
};

const videoCt = ref<HTMLElement>();
const showVideo = ref(false);
useIntersectionObserver(
  videoCt,
  ([observer]) => {
    if (observer?.isIntersecting && !showVideo.value) {
      showVideo.value = true;
    }
  },
  {
    // Espera a que el usuario baje lo suficiente para mostrar el video
    rootMargin:
      typeof window === "undefined"
        ? undefined
        : -(window.innerHeight / 100) * (isMobileSize.value ? 15 : 50) + "px",
  },
);
const mediaSectionCt = ref<HTMLElement>();
const showMediaSection = ref(false);
useIntersectionObserver(
  mediaSectionCt,
  ([observer]) => {
    if (observer?.isIntersecting && !showMediaSection.value) {
      showMediaSection.value = true;
      renderMedia();
    }
  },
  {
    // Espera a que el usuario baje lo suficiente para mostrar los PDF y etc.
    rootMargin:
      typeof window === "undefined"
        ? undefined
        : -(window.innerHeight / 100) * 30 + "px",
  },
);
const fixedShortDescription = props.product.short_description
  ?.replace(/\r\n/g, "\n")
  .trim(); // también ayuda con el SSR
const mounted = useMounted();
</script>

<template>
  <section
    v-if="
      product.video_link || product.short_description || product.description
    "
    :id="ProductDescriptionSection.Description"
  >
    <div ref="videoCt">
      <template v-if="product.video_link">
        <iframe
          v-if="product.video_link && showVideo"
          :src="product.video_link"
          class="mb-3 video-iframe"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
        <div
          v-else
          class="d-block bg-light w-100 d-flex align-items-center justify-content-center"
          style="height: 400px"
        >
          <i class="bi bi-youtube text-danger fs-1"></i>
        </div>
      </template>
    </div>
    <!-- SUMMARY -->
    <article v-if="fixedShortDescription" class="card mb-3 rounded-0">
      <h4 class="card-header bg-info text-white rounded-0 fw-bold">Resumen</h4>
      <div class="card-body">
        <p class="card-text text-body-secondary">
          {{ fixedShortDescription }}
        </p>
      </div>
    </article>
    <!-- DESCRIPTION -->
    <article v-if="product.description" class="card mb-3 rounded-0">
      <h4 class="card-header bg-info text-white rounded-0 fw-bold">
        Descripción
      </h4>
      <div class="card-body">
        <div class="card-text fs-5 text-body-secondary overflow-hidden">
          <safe-html :html="product.description" tag="div" />
        </div>
      </div>
    </article>
  </section>
  <section
    v-if="product.description_file || product.description_landing"
    :id="ProductDescriptionSection.Characteristics"
  >
    <!-- PDF -->
    <div ref="mediaSectionCt">
      <article
        v-if="product.description_file_is_pdf && showMediaSection"
        class="text-center mb-3"
      >
        <div v-if="!pdfReady" class="spinner-border text-info" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div v-show="pdfReady" ref="viewerCt">
          <div class="bg-light mb-2">
            <div ref="viewer" class="w-100 overflow-auto text-center" />
          </div>
          <div class="text-center">
            <a
              class="link-underline-opacity-10 link-info"
              rel="nofollow"
              :href="product.description_file || ''"
              download
            >
              Descargar PDF&nbsp;
              <i class="bi bi-box-arrow-up-right"></i>
            </a>
          </div>
        </div>
      </article>
      <!-- IMAGE -->
      <article
        v-else-if="product.description_file_is_image && showMediaSection"
        class="mb-3"
      >
        <img
          class="m-auto w-auto h-auto mw-100 mh-100 d-block"
          width="900"
          height="3000"
          :src="product.description_file || ''"
          :alt="product.meta_description || ''"
        />
      </article>
      <!-- LANDING -->
      <article
        v-if="product.description_landing && showMediaSection"
        class="mb-3 text-center"
      >
        <div
          v-show="!landingReady"
          class="spinner-border text-info"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
        <iframe
          v-show="landingReady"
          id="landingIFrame"
          class="w-100"
          width="100%"
          height="100%"
          frameborder="0"
          :src="product.description_landing"
          @load="landingReady = true"
        />
      </article>
    </div>
  </section>
  <section
    v-if="attributeGroupSorted.length > 0"
    :id="ProductDescriptionSection.Specifications"
  >
    <!-- ATTRIBUTES -->
    <article class="card mb-3 border-0">
      <h4
        class="card-header bg-info text-white rounded-0 fw-bold border-1 border border-info"
      >
        Especificaciones
      </h4>
      <div class="card-body px-0">
        <div class="row">
          <div
            v-for="(col, colIndex) in attributeGroupColumns"
            :key="colIndex"
            class="col-md-6"
          >
            <div
              :id="`attr-group-accordion-${colIndex}`"
              class="accordion border-0"
              style="
                --bs-accordion-btn-bg: var(--bs-gray-100);
                --bs-accordion-active-bg: var(--bs-gray-200);
              "
            >
              <div
                v-for="(group, groupIndex) in col"
                :key="groupIndex"
                class="accordion-item"
              >
                <h2 class="accordion-header">
                  <button
                    class="accordion-button px-3 py-2"
                    type="button"
                    data-bs-toggle="collapse"
                    :aria-expanded="initExpanded"
                    :aria-controls="`attr-group-collapse-${groupIndex}${colIndex}`"
                    :data-bs-target="`#attr-group-collapse-${groupIndex}${colIndex}`"
                    :class="{ collapsed: !initExpanded }"
                  >
                    {{ group[1].group.name }}
                  </button>
                </h2>
                <div
                  :id="`attr-group-collapse-${groupIndex}${colIndex}`"
                  class="accordion-collapse"
                  :class="[initExpanded ? 'show' : 'collapse']"
                  :data-bs-parent="
                    openOneAtATime ? `#attr-group-accordion-${colIndex}` : null
                  "
                >
                  <div class="accordion-body p-0">
                    <ul class="list-group list-group-flush">
                      <li
                        v-for="(attr, attrIndex) in group[1].attributes"
                        :key="`${groupIndex}${colIndex}${attrIndex}`"
                        class="list-group-item d-flex justify-content-between gap-2"
                      >
                        <small class="text-secondary">{{
                          attr.attribute_name
                        }}</small>
                        <small class="fw-semibold text-secondary text-end">{{
                          attr.value
                        }}</small>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  </section>
  <section v-if="mounted">
    <!-- REVIEWS -->
    <article class="card mb-3 border-0">
      <h4
        class="card-header bg-info text-white rounded-0 fw-bold border-1 border border-info mb-3"
      >
        Reseñas de clientes
      </h4>
      <product-reviews :product="product"></product-reviews>
    </article>
    <!-- QUESTIONS -->
    <article class="card mb-3 border-0">
      <h4
        class="card-header bg-info text-white rounded-0 fw-bold border-1 border border-info mb-3"
      >
        Preguntas de clientes
      </h4>
      <product-questions :product="product"></product-questions>
    </article>
  </section>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/bootstrap-helpers" as *;
iframe {
  width: 100%;
  height: 100vh;
}
.accordion-item {
  &:has(.accordion-header button[aria-expanded="true"]) {
    .accordion-header button {
      font-weight: bolder !important;
    }
  }
}
.video-iframe {
  width: 100%;
  height: 40vh;
  @include media-breakpoint-up(md) {
    height: 60vh;
  }
}
.accordion-item:last-of-type {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
</style>
